import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { seoAutopilotState, seoIntegrationStates } from "@/lib/seo/seo-os.functions";
import { SeoPage, Stat, Panel, Pill, Loading, Empty, stateTone, stateLabel, num } from "@/components/admin/seo/SeoUi";

export const Route = createFileRoute("/admin/seo/")({
  head: () => ({
    meta: [{ title: "SEO operating system · Admin" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: SeoOverview,
});

function SeoOverview() {
  const loadState = useServerFn(seoAutopilotState);
  const loadIntegrations = useServerFn(seoIntegrationStates);
  const state = useQuery({ queryKey: ["seo-autopilot-state"], queryFn: () => loadState() });
  const integrations = useQuery({ queryKey: ["seo-integrations"], queryFn: () => loadIntegrations() });

  const d = state.data as any;
  const i = integrations.data as any;

  return (
    <SeoPage
      title="SEO operating system"
      description="Live search performance, ranking opportunities and everything automation wants to change — nothing is applied without passing policy and compliance checks."
    >
      {state.isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              label="Striking distance (4–10)"
              value={num(d?.diagnosis?.queues?.top3 ?? 0)}
              sub="Searches close to the top three"
            />
            <Stat label="Just off page 1 (11–20)" value={num(d?.diagnosis?.queues?.page1 ?? 0)} />
            <Stat label="Lift candidates (21–50)" value={num(d?.diagnosis?.queues?.lift ?? 0)} />
            <Stat
              label="Awaiting your approval"
              value={num(d?.pending?.length ?? 0)}
              sub={d?.policy?.kill_switch ? "Automatic changes are OFF" : "Low-risk changes may auto-apply"}
            />
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <Panel title="What the system found" subtitle="Computed only from real Search Console data.">
              {d?.diagnosis?.findings?.length ? (
                <ul className="space-y-3">
                  {d.diagnosis.findings.map((f: any) => (
                    <li key={f.id} className="rounded-xl border border-border p-3">
                      <div className="flex items-center gap-2">
                        <Pill tone={f.severity === "critical" ? "bad" : f.severity === "warning" ? "warn" : "neutral"}>
                          {f.severity}
                        </Pill>
                        <span className="text-sm font-semibold">{f.title}</span>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">{f.detail}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <Empty>No findings yet.</Empty>
              )}
            </Panel>

            <Panel title="Connections" subtitle="Exactly what is connected and whether data has actually arrived.">
              {integrations.isLoading ? (
                <Loading />
              ) : (
                <ul className="space-y-2 text-sm">
                  {[
                    { label: "Google Search Console", state: i?.gsc?.state, detail: i?.gsc?.detail },
                    {
                      label: "Google Analytics 4",
                      state: i?.ga4?.state,
                      detail: i?.ga4?.measurementId ? "Measurement tag active." : "No measurement ID configured.",
                    },
                    {
                      label: "Instant indexing (IndexNow)",
                      state: i?.indexnow?.state,
                      detail: i?.indexnow?.state === "ok" ? `Host: ${i?.host}` : "Needs an IndexNow key.",
                    },
                    { label: "Ranking provider", state: i?.rank?.state, detail: i?.rank?.detail },
                  ].map((row) => (
                    <li key={row.label} className="flex items-start justify-between gap-3 rounded-xl border border-border p-3">
                      <div>
                        <p className="font-semibold">{row.label}</p>
                        <p className="text-xs text-muted-foreground">{row.detail}</p>
                      </div>
                      <Pill tone={stateTone(row.state ?? "unconfigured")}>{stateLabel(row.state ?? "unconfigured")}</Pill>
                    </li>
                  ))}
                </ul>
              )}
              <Link to="/admin/seo/integrations" className="mt-3 inline-block text-sm font-semibold text-brand underline">
                Manage connections
              </Link>
            </Panel>
          </div>

          <div className="mt-4">
            <Panel title="Recent automation runs">
              {d?.runs?.length ? (
                <ul className="space-y-2 text-sm">
                  {d.runs.slice(0, 8).map((r: any) => (
                    <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border p-3">
                      <span className="font-medium">{new Date(r.started_at).toLocaleString()}</span>
                      <span className="text-muted-foreground">
                        {r.actions_created ?? 0} suggested · {r.actions_applied ?? 0} applied · {r.actions_blocked ?? 0} blocked
                      </span>
                      <Pill tone={r.status === "completed" ? "good" : r.status === "error" ? "bad" : "warn"}>{r.status}</Pill>
                    </li>
                  ))}
                </ul>
              ) : (
                <Empty>No runs yet. Start one from the Autopilot screen.</Empty>
              )}
            </Panel>
          </div>
        </>
      )}
    </SeoPage>
  );
}
